/**
 * Model widoku ekranu prezentacji (tryb kiosku, bez logowania).
 *
 * Czyste funkcje bez Reacta — ekran dostaje z backendu plaska liste meczow
 * i sam rozklada ja na sekcje makiety: mecze w trakcie, nastepne i ostatnie
 * wyniki. Nazwiska przychodza gotowe w slowniku `players`, wiec w odroznieniu
 * od widokow klubowych nie ma tu batcha do `user-service`.
 */

import { bracketRoundLabel, isResolved } from './drawView';
import type { PublicCompetitionSummary, PublicPlayer } from './publicTypes';
import { shortName } from './dashboardView';
import type { ApiBracket, ApiMatch } from './types';

/**
 * Buduje mape `userId -> krotka nazwa` ze slownika publicznego.
 *
 * Backend przysyla pelne „Imie Nazwisko”, a karty meczu sa waskie — skracamy
 * do inicjalu tak samo jak widoki klubowe. Pierwszy czlon to imie, reszta
 * nazwisko (nazwiska dwuczlonowe zostaja w calosci).
 */
export function buildPublicNameMap(players: readonly PublicPlayer[]): Map<string, string> {
  return new Map(
    players.map((player) => {
      const trimmed = player.displayName.trim();
      const spaceAt = trimmed.indexOf(' ');
      if (spaceAt <= 0) return [player.userId, trimmed] as const;
      return [
        player.userId,
        shortName(trimmed.slice(0, spaceAt), trimmed.slice(spaceAt + 1)),
      ] as const;
    }),
  );
}

/** Czy mecz ma obsadzone obie strony (pusty slot drabinki czeka na awans). */
export function hasBothSides(match: ApiMatch): boolean {
  return match.team1Ids.length > 0 && match.team2Ids.length > 0;
}

/** Klucz sortowania po terminie — mecze bez terminu ida na koniec. */
function scheduleKey(match: ApiMatch): string {
  if (!match.scheduledDate) return '9999-12-31T99:99';
  return `${match.scheduledDate}T${match.scheduledTime ?? '99:99'}`;
}

/** Klucz sortowania „najswiezsze pierwsze” dla rozstrzygnietych meczow. */
function resolvedKey(match: ApiMatch): string {
  return match.scoreEnteredAt ?? match.endedAt ?? match.startedAt ?? '';
}

/** Sekcje meczowe ekranu prezentacji. */
export interface ScreenMatches {
  /** Mecze rozgrywane w tej chwili. */
  live: ApiMatch[];
  /** Najblizsze mecze z obsadzonymi obiema stronami. */
  upcoming: ApiMatch[];
  /** Ostatnio rozstrzygniete mecze, od najswiezszego. */
  recent: ApiMatch[];
}

/**
 * Rozklada mecze rozgrywki na sekcje makiety.
 *
 * Do „nastepnych” trafiaja wylacznie mecze z obsadzonymi obiema stronami:
 * pusta para drabinki to jeszcze nie mecz, ktory da sie zapowiedziec widzowi.
 */
export function splitScreenMatches(matches: readonly ApiMatch[]): ScreenMatches {
  const live: ApiMatch[] = [];
  const upcoming: ApiMatch[] = [];
  const recent: ApiMatch[] = [];

  for (const match of matches) {
    if (match.status === 'IN_PROGRESS' || match.status === 'SUSPENDED') {
      live.push(match);
    } else if (isResolved(match.status) && match.status !== 'CANCELLED') {
      recent.push(match);
    } else if (match.status === 'SCHEDULED' && hasBothSides(match)) {
      upcoming.push(match);
    }
  }

  live.sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0));
  upcoming.sort(
    (a, b) =>
      scheduleKey(a).localeCompare(scheduleKey(b)) || (a.matchNumber ?? 0) - (b.matchNumber ?? 0),
  );
  recent.sort(
    (a, b) =>
      resolvedKey(b).localeCompare(resolvedKey(a)) || (b.matchNumber ?? 0) - (a.matchNumber ?? 0),
  );

  return { live, upcoming, recent };
}

/**
 * Ile dni wstecz siega sekcja „ostatnie wyniki” na zakladce „Info ogolne”.
 *
 * Impreza klubowa skleja mecze ze wszystkich rozgrywek klubu naraz — bez okna
 * czasowego lista ciagnelaby wyniki sprzed miesiecy i przestawala byc
 * informacja o tym, co dzieje sie teraz.
 */
export const RECENT_RESULTS_WINDOW_DAYS = 14;

/**
 * Znacznik rozstrzygniecia meczu — ta sama kolejnosc zrodel co przy sortowaniu
 * wynikow, z terminem z planu jako ostatnia deska ratunku.
 */
function resolvedAt(match: ApiMatch): string | null {
  return match.scoreEnteredAt ?? match.endedAt ?? match.startedAt ?? match.scheduledDate ?? null;
}

/**
 * Zawezaj rozstrzygniete mecze do okna ostatnich `days` dni.
 *
 * Mecz bez jakiegokolwiek znacznika czasu odpada — nie da sie potwierdzic, ze
 * miesci sie w oknie, a sekcja ma pokazywac wylacznie swieze wyniki.
 */
export function recentWithinDays(
  matches: readonly ApiMatch[],
  days: number = RECENT_RESULTS_WINDOW_DAYS,
  now: Date = new Date(),
): ApiMatch[] {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  return matches.filter((match) => {
    const stamp = resolvedAt(match);
    if (!stamp) return false;
    const decidedAt = Date.parse(stamp);
    return !Number.isNaN(decidedAt) && decidedAt >= cutoff;
  });
}

/** Postep rozgrywki liczony w meczach (pasek „18 / 31 meczów”). */
export interface ScreenProgress {
  resolved: number;
  total: number;
}

/**
 * Liczy postep turnieju. Mianownikiem sa mecze faktycznie istniejace —
 * przed losowaniem nie ma ich wcale i sekcja postepu sie nie pokazuje.
 */
export function screenProgress(matches: readonly ApiMatch[]): ScreenProgress {
  return {
    resolved: matches.filter((match) => isResolved(match.status)).length,
    total: matches.length,
  };
}

/**
 * Nazwa rundy, ktora wlasnie sie toczy — pierwsza od poczatku drabinki
 * z nierozstrzygnietym meczem. Gdy wszystko rozegrane, zwraca ostatnia runde
 * (turniej zakonczony finalem).
 */
export function currentRoundLabel(bracket: ApiBracket | undefined): string | null {
  if (!bracket) return null;

  const rounds = bracket.rounds
    .filter((round) => round.name !== '3P')
    .slice()
    .sort((a, b) => a.roundNumber - b.roundNumber);
  if (rounds.length === 0) return null;

  const labelOf = (index: number): string | null => {
    const round = rounds[index];
    if (!round) return null;
    return bracketRoundLabel(round.name, bracket.bracketSize / 2 ** (index + 1));
  };

  const pendingIndex = rounds.findIndex((round) =>
    round.matches.some((match) => !isResolved(match.status)),
  );
  return labelOf(pendingIndex >= 0 ? pendingIndex : rounds.length - 1);
}

/**
 * Turniej domyslnie wybrany po wejsciu na ekran: pierwszy z listy.
 * Backend sortuje ja „najpierw to, co sie gra”, wiec bez dodatkowej logiki
 * ekran startuje od trwajacej rozgrywki.
 */
export function defaultCompetitionId(
  competitions: readonly PublicCompetitionSummary[],
): string | null {
  return competitions[0]?.id ?? null;
}

/** Dzisiejsza data w formacie ISO (`yyyy-MM-dd`) — parametr planu kortow. */
export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
